import React from 'react';
import {Button, Drawer, Form, Input, Select, Tag, SelectProps, Row} from 'antd';
import _ from 'lodash';

import {messageType, showMessages} from '../../../helpers/message';
import {nothingToUpdate} from '../../../helpers/text';
import {useCreateStaffProfileMutation, useUpdateStaffProfileMutation} from '../../../store/api/staffManagementApi';
import {
    AssignableStaffRoles,
    StaffManagementDrawerProps,
    StaffProfileCreateParams,
    staffRolesMap,
    RolesSelect,
    CustomTagRenderProps
} from '../../../types/staffManagementTypes';
import {BaseError} from '../../../types/apiTypes';

import './staffManagement.less';

type TagRender = SelectProps['tagRender'];

const StaffManagementDrawer = ({profile, drawerOpen, action, handleClose}: StaffManagementDrawerProps) => {
    const mobileMode = window.innerWidth <= 768;
    const drawerWidth = mobileMode ? "100%" : "45%";
    const [form] = Form.useForm();

    const [createProfile, {isLoading: createLoading, isSuccess: createSuccess, isError: isCreateError, error: createError, reset: createReset}] = useCreateStaffProfileMutation();
    const [updateProfile, {isLoading: updateLoading, isSuccess: updateSuccess, isError: isUpdateError, error: updateError, reset: updateReset}] = useUpdateStaffProfileMutation();

    // Build the F/E friendly display list of staff roles for the dropdown menu and tags. Sort alphabetically.
    const rolesSelectOptions: RolesSelect = (Object.keys(staffRolesMap) as AssignableStaffRoles[]).map(role => {
        return {label: staffRolesMap[role].name, value: role};
    }).sort((a, b) => a.label.localeCompare(b.label));

    const resetDrawer = React.useCallback(() => {
        form.resetFields();
        // There's a browser error where closing the edit drawer is retaining the roles so clear them explicitly.
        form.setFieldsValue({'roles': []});
        handleClose();
    }, [form, handleClose]);

    const handleFinish = (values: StaffProfileCreateParams) => {
        const {first_name, last_name, email, roles} = values;

        if (form.isFieldsTouched()) {
            if (action === 'Add') {
                createProfile({
                    first_name,
                    last_name,
                    email,
                    roles,
                });
            } else if (action === 'Update' && profile) {

                // If user submits with nothing to update then exit.
                // Compare first name, last name, email, and then jagged array of roles.
                if (_.isEqual(_.omit(values, ['roles']), _.omit(profile, ['id', 'active', 'roles']))
                    && _.xor(values['roles'], profile['roles']).length === 0
                ) {
                    showMessages(nothingToUpdate, messageType.error);
                    return;
                }

                updateProfile({
                    profile_id: profile.id,
                    first_name,
                    last_name,
                    email,
                    roles,
                });
            }
        }
    }

    const tagRender: TagRender = (props: CustomTagRenderProps) => {
        const { value: role, closable, onClose } = props;

        return (
            <Tag
                className="staff-manage-drawer-select-role-tag"
                color={staffRolesMap[role].color}
                closable={closable}
                onClose={onClose}
            >
                {staffRolesMap[role].name}
            </Tag>
        );
    };

    React.useEffect(() => {
        if (createSuccess) {
            showMessages('Successfully created.', messageType.success);
            createReset();
            resetDrawer();
        } else if (isCreateError) {
            showMessages(((createError) as BaseError).data?.message, messageType.error);
            createReset();
        }
    }, [createError, createReset, createSuccess, handleClose, isCreateError, resetDrawer]);

    React.useEffect(() => {
        if (updateSuccess) {
            showMessages('Staff member has been successfully updated.', messageType.success);
            updateReset();
            resetDrawer();
        } else if (isUpdateError) {
            showMessages(((updateError) as BaseError).data?.message, messageType.error);
            updateReset();
        }
    }, [updateSuccess, isUpdateError, updateError, updateReset, resetDrawer]);

    React.useEffect(() => {
        // ForceRender prop on Drawer is needed to stop a useForm error, which causes an issue with setFieldValue,
        //  needing it to be conditionally called in this useEffect.
        if (action === 'Update' && profile) {
            form.setFieldsValue({
                first_name: profile.first_name,
                last_name: profile.last_name,
                email: profile.email,
                roles: profile.roles
            });
        }
    }, [profile, action, form]);

    return (
        <Drawer
            title={`${action === 'Update' ? 'Edit' : 'Add'} Staff`}
            className="staff-manage-drawer-wrapper"
            width={drawerWidth}
            open={drawerOpen}
            onClose={resetDrawer}
            forceRender={true}
        >
            <Form
                name="staff_manage_drawer"
                layout="vertical"
                form={form}
                onFinish={handleFinish}
                initialValues={{'roles': profile?.roles}}
            >
                <Row className="full-width-row">
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{
                          type: 'string',
                          required: true,
                          message: 'Please enter a first name.'
                        }]}
                    >
                        <Input placeholder="Type here" name="first_name"/>
                    </Form.Item>
                </Row>
                <Row className="full-width-row">
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{
                            type: 'string',
                            required: true,
                            message: 'Please enter a last name.'
                        }]}
                    >
                        <Input placeholder="Type here" name="last_name"/>
                    </Form.Item>
                </Row>
                <Row className="full-width-row">
                    <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[{
                            type: 'string',
                            required: true,
                            message: 'Please enter a valid email address.'
                        }]}
                    >
                        <Input placeholder="Type here" name="email"/>
                    </Form.Item>
                </Row>
                {action === 'Add' && (
                    <Row className="password-row">
                        <Form.Item
                            label="Password"
                            extra={
                                <div>
                                    A randomized password is automatically set.
                                    Please instruct staff to click on 'Forgot Password'
                                    to reset their password when they log in for the first time.
                                </div>
                            }
                        />
                    </Row>
                )}
                <Row className="full-width-row">
                    <Form.Item
                        name="roles"
                        label="Role"
                        rules={[{
                            type: 'array',
                            required: true,
                            message: "Please select a minimum of one role."
                        }]}
                    >
                        <Select
                            mode="multiple"
                            tagRender={tagRender}
                            placeholder="Select"
                            options={rolesSelectOptions}
                        />
                    </Form.Item>
                </Row>
                <Row className="staff-manage-drawer-buttons-row">
                    <Button
                        type="default"
                        onClick={resetDrawer}
                        className="staff-manage-drawer-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={createLoading || updateLoading}
                    >
                        {action + ' Staff'}
                    </Button>
                </Row>
            </Form>
        </Drawer>
    );
}

export default StaffManagementDrawer;
